

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  FaEye,
  FaHandshake,
  FaTrophy,
  FaHeart,
  FaChartLine,
  FaUsers,
  FaClock,
  FaShieldAlt,
  FaArrowRight,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: FaEye,
      title: 'Vision',
      description:
        'To be the most trusted financial partner for every Indian family.',
    },
    {
      icon: FaHandshake,
      title: 'Mission',
      description:
        'To provide transparent, ethical, and profitable financial solutions.',
    },
    {
      icon: FaTrophy,
      title: 'Excellence',
      description:
        'Striving for excellence in every client interaction and investment.',
    },
    {
      icon: FaHeart,
      title: 'Care',
      description:
        'Putting client interests first with personalized attention.',
    },
  ];

  const team = [
    {
      name: 'Yagnesh Dole',
      role: 'Founder & Financial Advisor',
      experience: '15+ years',
      expertise: 'Investment Planning, Retirement',
    },
    {
      name: 'Rajesh Patel',
      role: 'Senior Investment Advisor',
      experience: '12+ years',
      expertise: 'Mutual Funds, Portfolio Management',
    },
    {
      name: 'Priya Mehta',
      role: 'Insurance Specialist',
      experience: '10+ years',
      expertise: 'Life & Health Insurance',
    },
    {
      name: 'Amit Shah',
      role: 'Tax & Loan Expert',
      experience: '8+ years',
      expertise: 'Tax Planning, Home Loans',
    },
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Wisdom Consultancy</title>
      </Helmet>

      <section className="relative overflow-hidden bg-[#f5f7f6]">
        {/* GRID BACKGROUND */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              linear-gradient(to right, #d7d7d7 1px, transparent 1px),
              linear-gradient(to bottom, #d7d7d7 1px, transparent 1px)
            `,
            backgroundSize: '42px 42px',
          }}
        />

        <div className="container-custom relative z-10 py-20">
          {/* HERO SECTION */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* LEFT SIDE */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h1 className="text-5xl md:text-7xl font-extrabold leading-tight text-[#222]">
                Who{' '}
                <span className="text-[#69bf74]">
                  We Are
                </span>
              </h1>

              <h2 className="mt-5 text-2xl md:text-3xl font-semibold text-[#2e56b8]">
                Your Partner in Financial Growth & Protection
              </h2>

              <p className="mt-8 text-lg md:text-xl text-gray-600 leading-9 max-w-2xl">
                Wisdom Consultancy provides customized financial solutions
                including Mutual Funds, SIPs, Insurance, Retirement Planning,
                and Wealth Management. Our mission is to help clients make
                confident financial decisions and secure their future with
                strategic planning.
              </p>

              <p className="mt-4 text-lg md:text-xl text-gray-600 leading-9 max-w-2xl font-semibold">
                Yagneshkumar Rajeshbhai Dole is an AMFI Registered Mutual Fund Distributor bearing ARN 185896.
              </p>

              {/* BUTTON */}
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 bg-[#2e56b8] hover:bg-[#24479c] text-white px-8 py-5 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg"
                >
                  Book Free Consultation
                  <FaArrowRight />
                </Link>
              </div>
            </motion.div>

            {/* RIGHT SIDE GRAPH DESIGN */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="relative"
            >
              {/* BACKGROUND BOX */}
              <div className="absolute top-10 left-10 w-full h-full bg-[#dff0e7] rounded-[40px]" />

              {/* MAIN CARD */}
              {/* <div className="relative bg-white rounded-[35px] p-8 shadow-2xl max-w-[520px]"> */}
              <div className="relative bg-white rounded-[35px] pt-20 pb-8 px-8 shadow-2xl max-w-[520px]">
                {/* TOP */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-[#222]">
                      Growth
                    </h3>

                    <p className="text-gray-500 text-sm">
                      Higher Than Last Week
                    </p>
                  </div>

                  <h2 className="text-3xl font-bold text-[#222]">
                    1,500.98
                  </h2>
                </div>

                {/* GRAPH */}
                <div className="relative h-[220px]">
                  {/* Y AXIS */}
                  <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400">
                    <span>8 AM</span>
                    <span>6 AM</span>
                    <span>4 AM</span>
                    <span>2 AM</span>
                  </div>

                  {/* GRAPH BARS */}
                  <div className="ml-12 h-full flex items-end justify-between gap-4">
                    {[70, 55, 78, 85, 50, 110, 135].map((height, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col items-center gap-3"
                      >
                        <div
                          className="w-4 rounded-full bg-gradient-to-t from-[#69bf74] to-[#d7f0db] shadow-md"
                          style={{ height: `${height}px` }}
                        />

                        <span className="text-xs text-gray-500">
                          {
                            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][
                              idx
                            ]
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SMALL CARD */}
                <div className="absolute -top-10 right-0 bg-[#e8f4ee] rounded-[28px] p-5 shadow-lg w-[220px]">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#69bf74]">
                      <FaUsers />
                    </div>

                    <div>
                      <h3 className="text-2xl font-bold text-[#69bf74]">
                        19+
                      </h3>

                      <p className="text-xs text-gray-600">
                        Years Of Experience
                      </p>
                    </div>
                  </div>
                </div>

                {/* RIGHT MINI BOXES */}
                <div className="absolute top-36 -right-10 space-y-4">
                  <div className="bg-[#edf3ff] rounded-2xl px-6 py-5 shadow-lg w-[170px]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#2e56b8]">
                        <FaChartLine />
                      </div>

                      <div>
                        <h4 className="font-bold text-[#2e56b8]">
                          Financial
                        </h4>

                        <p className="text-xs text-gray-500">
                          Planning
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#e8f4ee] rounded-2xl px-6 py-5 shadow-lg w-[170px]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#69bf74]">
                        <FaShieldAlt />
                      </div>

                      <div>
                        <h4 className="font-bold text-[#69bf74]">
                          Wealth
                        </h4>

                        <p className="text-xs text-gray-500">
                          Management
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* VALUES SECTION */}
          <div className="mt-32">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-[#222]">
                Our Core{' '}
                <span className="text-[#2e56b8]">
                  Values
                </span>
              </h2>

              <p className="mt-5 text-lg text-gray-600">
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-[30px] p-8 shadow-md hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-20 h-20 rounded-3xl bg-[#edf3ff] flex items-center justify-center mb-6">
                    <value.icon className="text-4xl text-[#2e56b8]" />
                  </div>

                  <h3 className="text-2xl font-bold text-[#222] mb-4">
                    {value.title}
                  </h3>

                  <p className="text-gray-600 leading-8">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* WHY CHOOSE US */}
          <div className="mt-32 grid lg:grid-cols-2 gap-16">
            {/* LEFT */}
            <div>
              <h2 className="text-5xl font-bold text-[#222] mb-10">
                Why Choose{' '}
                <span className="text-[#69bf74]">
                  Us?
                </span>
              </h2>

              <div className="space-y-6">
                {[
                  {
                    icon: FaShieldAlt,
                    title: 'Trusted & Transparent',
                    desc:
                      'We believe in complete transparency in all our dealings.',
                  },
                  {
                    icon: FaChartLine,
                    title: 'Proven Track Record',
                    desc:
                      'Consistent returns and satisfied clients for over a decade.',
                  },
                  {
                    icon: FaUsers,
                    title: 'Personalized Approach',
                    desc:
                      'Custom solutions tailored to your unique financial goals.',
                  },
                  {
                    icon: FaClock,
                    title: 'Timely Support',
                    desc:
                      'Quick response and dedicated support whenever you need.',
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-[28px] p-6 shadow-md flex gap-5"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-[#edf3ff] flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-2xl text-[#2e56b8]" />
                    </div>

                    <div>
                      <h3 className="text-2xl font-bold text-[#222] mb-2">
                        {item.title}
                      </h3>

                      <p className="text-gray-600 leading-7">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT */}
            <div>
              <h2 className="text-5xl font-bold text-[#222] mb-10">
                Our{' '}
                <span className="text-[#2e56b8]">
                  Expertise
                </span>
              </h2>

              <div className="bg-white rounded-[35px] p-10 shadow-xl space-y-8">
                {[
                  {
                    title: 'Mutual Funds & SIP',
                    value: '95%',
                    width: '95%',
                  },
                  {
                    title: 'Life Insurance',
                    value: '90%',
                    width: '90%',
                  },
                  {
                    title: 'Health Insurance',
                    value: '88%',
                    width: '88%',
                  },
                  {
                    title: 'Wealth Management',
                    value: '85%',
                    width: '85%',
                  },
                  {
                    title: 'Home Loans',
                    value: '82%',
                    width: '82%',
                  },
                  {
                    title: 'Financial Planning',
                    value: '83%',
                    width: '83%',
                  },
                ].map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-3">
                      <span className="font-semibold text-[#222]">
                        {item.title}
                      </span>

                      <span className="font-bold text-[#69bf74]">
                        {item.value}
                      </span>
                    </div>

                    <div className="w-full h-4 bg-[#edf3ff] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#2e56b8] to-[#69bf74]"
                        style={{ width: item.width }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TEAM SECTION */}
          <div className="mt-32">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-[#222]">
                Meet Our{' '}
                <span className="text-[#69bf74]">
                  Experts
                </span>
              </h2>

              <p className="mt-5 text-lg text-gray-600">
                Dedicated professionals committed to your financial success
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-[30px] p-8 shadow-md hover:shadow-2xl transition-all duration-300 text-center"
                >
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#2e56b8] to-[#69bf74] mx-auto flex items-center justify-center text-white text-4xl font-bold mb-6">
                    {member.name.charAt(0)}
                  </div>

                  <h3 className="text-2xl font-bold text-[#222] mb-2">
                    {member.name}
                  </h3>

                  <p className="text-[#2e56b8] font-semibold mb-2">
                    {member.role}
                  </p>

                  <p className="text-gray-500 mb-2">
                    {member.experience} Experience
                  </p>

                  <p className="text-gray-600 text-sm leading-7">
                    {member.expertise}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
// import React from 'react';
// import { Helmet } from 'react-helmet-async';
// import { motion } from 'framer-motion';
// import { Link } from 'react-router-dom';

// import {
//   FaShieldAlt,
//   FaChartLine,
//   FaUsers,
//   FaHandshake,
//   FaArrowRight,
//   FaBullseye,
// } from 'react-icons/fa';

// const About = () => {
//   const features = [
//     {
//       title: 'Expert Guidance',
//       description:
//         'Professional investment and insurance solutions backed by 19+ years of industry experience.',
//       icon: <FaChartLine />,
//     },
//     {
//       title: 'Client-Centric',
//       description:
//         'Every solution is customized according to your financial goals and future needs.',
//       icon: <FaUsers />,
//     },
//     {
//       title: 'Goal-Based Wealth Planning',
//       description:
//         'Strategic financial planning designed for education, retirement, wealth creation, and family security.',
//       icon: <FaBullseye />,
//     },
//     {
//       title: 'Transparent & Ethical',
//       description:
//         'We believe in honest guidance, complete transparency, and long-term client relationships.',
//       icon: <FaHandshake />,
//     },
//   ];

//   const expertise = [
//     'Mutual Funds & SIP',
//     'Life Insurance',
//     'Health Insurance',
//     'Wealth Management',
//     'Home Loans',
//     'Financial Planning',
//   ];

//   const stats = [
//     { value: '500+', label: 'Happy Clients' },
//     { value: '1000+', label: 'Investments Done' },
//     { value: '19+', label: 'Years Experience' },
//     { value: '50Cr+', label: 'Wealth Managed' },
//   ];

//   return (
//     <>
//       <Helmet>
//         <title>About Us - Wisdom Consultancy</title>

//         <meta
//           name="description"
//           content="Wisdom Consultancy provides customized financial solutions including Mutual Funds, SIPs, Insurance, Retirement Planning, and Wealth Management."
//         />
//       </Helmet>

//       <section className="relative overflow-hidden bg-[#f5f7f6]">

//         {/* Background Grid */}
//         <div
//           className="absolute inset-0 opacity-40"
//           style={{
//             backgroundImage: `
//               linear-gradient(to right, #d7d7d7 1px, transparent 1px),
//               linear-gradient(to bottom, #d7d7d7 1px, transparent 1px)
//             `,
//             backgroundSize: '42px 42px',
//           }}
//         />

//         <div className="container-custom relative z-10 py-20">

//           {/* Hero Section */}
//           <div className="grid lg:grid-cols-2 gap-14 items-center">

//             {/* Left Content */}
//             <motion.div
//               initial={{ opacity: 0, x: -40 }}
//               animate={{ opacity: 1, x: 0 }}
//             >

//               <div className="inline-flex items-center gap-3 bg-white shadow-md px-5 py-3 rounded-full mb-8">
//                 <div className="w-10 h-10 rounded-full bg-[#2e56b8] flex items-center justify-center text-white">
//                   <FaShieldAlt />
//                 </div>

//                 <span className="font-semibold text-[#0b1c39]">
//                   About Wisdom Consultancy
//                 </span>
//               </div>

//               <h1 className="text-5xl md:text-7xl font-extrabold leading-tight text-[#222]">
//                 Who{' '}
//                 <span className="text-[#69bf74]">
//                   We Are
//                 </span>
//               </h1>

//               <h2 className="mt-5 text-2xl md:text-3xl font-semibold text-[#2e56b8]">
//                 Your Partner in Financial Growth & Protection
//               </h2>

//               <p className="mt-8 text-lg md:text-xl text-gray-600 leading-9 max-w-2xl">
//                 Wisdom Consultancy provides customized financial solutions including Mutual Funds, SIPs, Insurance, Retirement Planning, and Wealth Management. Our mission is to help clients make confident financial decisions and secure their future with strategic planning.
//               </p>

//               <div className="mt-10">
//                 <Link
//                   to="/contact"
//                   className="inline-flex items-center gap-3 bg-[#2e56b8] hover:bg-[#24479c] text-white px-8 py-5 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg"
//                 >
//                   Book Free Consultation
//                   <FaArrowRight />
//                 </Link>
//               </div>
//             </motion.div>

//             {/* Right Side Card */}
//             <motion.div
//               initial={{ opacity: 0, x: 40 }}
//               animate={{ opacity: 1, x: 0 }}
//             >
//               <div className="bg-white rounded-[35px] p-10 shadow-2xl border border-[#edf3ff]">

//                 <h3 className="text-3xl font-bold text-[#222] mb-8">
//                   Our Expertise
//                 </h3>

//                 <div className="space-y-5">
//                   {expertise.map((item, index) => (
//                     <div
//                       key={index}
//                       className="flex items-center justify-between bg-[#f8fbff] px-6 py-4 rounded-2xl"
//                     >
//                       <span className="font-semibold text-[#222]">
//                         {item}
//                       </span>

//                       <div className="w-3 h-3 rounded-full bg-[#69bf74]" />
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </motion.div>
//           </div>

//           {/* Why Choose Us */}
//           <div className="mt-32">

//             <div className="text-center mb-16">

//               <h2 className="text-4xl md:text-5xl font-bold text-[#222] mb-5">
//                 Why Choose{' '}
//                 <span className="text-[#2e56b8]">
//                   Wisdom Consultancy?
//                 </span>
//               </h2>

//               <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-8">
//                 We provide comprehensive financial solutions tailored to your goals and future financial security.
//               </p>
//             </div>

//             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
//               {features.map((feature, index) => (
//                 <motion.div
//                   key={index}
//                   initial={{ opacity: 0, y: 25 }}
//                   whileInView={{ opacity: 1, y: 0 }}
//                   transition={{ delay: index * 0.1 }}
//                   className="bg-white rounded-[30px] p-8 shadow-md hover:shadow-2xl transition-all duration-300 border border-transparent hover:border-[#dbe7ff] text-center"
//                 >
//                   <div className="w-20 h-20 rounded-3xl bg-[#edf3ff] flex items-center justify-center text-4xl text-[#2e56b8] mx-auto mb-6">
//                     {feature.icon}
//                   </div>

//                   <h3 className="text-2xl font-bold text-[#0b1c39] mb-4">
//                     {feature.title}
//                   </h3>

//                   <p className="text-gray-600 leading-8">
//                     {feature.description}
//                   </p>
//                 </motion.div>
//               ))}
//             </div>
//           </div>

//           {/* Stats */}
//           <div className="mt-32">
//             <div className="bg-white rounded-[40px] shadow-2xl p-10 md:p-16 border border-[#e7f0eb]">

//               <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
//                 {stats.map((stat, index) => (
//                   <motion.div
//                     key={index}
//                     initial={{ opacity: 0, scale: 0.5 }}
//                     whileInView={{ opacity: 1, scale: 1 }}
//                     transition={{ delay: index * 0.1 }}
//                   >
//                     <div className="text-4xl md:text-5xl font-bold text-[#2e56b8] mb-3">
//                       {stat.value}
//                     </div>

//                     <div className="text-gray-600 text-lg">
//                       {stat.label}
//                     </div>
//                   </motion.div>
//                 ))}
//               </div>
//             </div>
//           </div>

//           {/* CTA */}
//           <div className="mt-32">
//             <div className="bg-white rounded-[40px] shadow-2xl p-10 md:p-16 text-center border border-[#e7f0eb]">

//               <h2 className="text-4xl md:text-5xl font-bold text-[#222] leading-tight mb-6">
//                 Book a Free Consultation with{' '}
//                 <span className="text-[#69bf74]">
//                   Financial Experts
//                 </span>
//               </h2>

//               <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-9 mb-10">
//                 Strategic investment and insurance planning tailored to your future needs.
//               </p>

//               <Link
//                 to="/contact"
//                 className="inline-flex items-center justify-center gap-3 bg-[#2e56b8] hover:bg-[#24479b] text-white px-8 py-5 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg"
//               >
//                 Get Started
//                 <FaArrowRight />
//               </Link>
//             </div>
//           </div>

//         </div>
//       </section>
//     </>
//   );
// };

// export default About;